import React, { useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft } from "icons";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H4M } from "typography/headers";

import Option from "./Option";

export interface OptionGroupProps {
  options: {
    value: string;
    label: string;
  }[];
  label: string;
  onSelect: (value: string) => void;
  value: string[];
  multi: boolean;
  translate: (key: string, options?: Record<string, string | number>) => string;
  searchTerm?: string;
}

const OptionGroup: React.FC<OptionGroupProps> = ({
  options,
  label,
  value,
  searchTerm,
  ...optProps
}) => {
  const [open, setOpen] = useState(false);
  const autoOpen = useRef(true);
  const handleToggle = useCallback(() => {
    autoOpen.current = false;
    setOpen((o) => !o);
  }, []);

  useEffect(() => {
    if ((searchTerm?.length ?? 0) >= 2 && options.length === 1) {
      setOpen(true);
      autoOpen.current = true;
    } else if (options.length > 1 && autoOpen.current) {
      setOpen(false);
    }
  }, [searchTerm, options.length]);

  return (
    <>
      <OptionGroupWrapper
        aria-expanded={open}
        role="button"
        onClick={handleToggle}
        $open={open}
        aria-label={`${label} (${options.length})`}
      >
        <H4M as="span">
          {label} ({options.length})
        </H4M>
        <Arrow $open={open} />
      </OptionGroupWrapper>
      {open &&
        options.map((option) => (
          <Option
            key={option.value}
            selected={value.includes(option.value)}
            {...option}
            {...optProps}
          />
        ))}
    </>
  );
};

const Arrow = styled(ChevronLeft)<{ $open: boolean }>`
  margin-left: auto;
  transform: rotate(${(props) => (props.$open ? "90" : "-90")}deg);
  transition: transform 0.4s ease;
  color: ${color.blue[300]};
  width: 20px;
  height: 20px;
`;
const OptionGroupWrapper = styled.div<{ $open: boolean }>`
  background: ${color.neutral[100]};
  border: 1px solid ${color.border.default};
  ${(props) =>
    props.$open &&
    css`
      background: ${color.neutral[100]};
      border: 1px solid ${color.neutral[300]};
    `}
  transition: background-color 0.2s ease, border-color 0.2s ease;
  border-radius: 8px;
  padding: 18px 16px;
  margin: 16px 8px 8px 16px;
  &:first-child {
    margin-top: 0;
  }
  cursor: pointer;
  display: flex;
  align-items: center;
`;

export default OptionGroup;
